/**
 * @poc-to-production
 *
 * Vera web-app root.
 */
import { useEffect, useMemo } from "preact/hooks";
import { isAuthDisabled } from "../auth/apsConfig";
import { isUniversalSheet } from "../data/sheets";
import { useAccLiveModel } from "../hooks/useAccLiveModel";
import { useLiveAccAnalysis } from "../hooks/useLiveAccAnalysis";
import { liveSheetByOntologyType } from "../lib/liveSheetRouting";
import { PRODUCT_NAME } from "../productBrand";
import { useAccBrowseStore } from "../stores/accBrowseStore";
import { useAccModelStore } from "../stores/accModelStore";
import { computeLiveViolations, useEngineStore } from "../stores/engineStore";
import { geometryViolations, useGeometryStore } from "../stores/geometryStore";
import { useCurrentProject } from "../stores/projectStore";
import { usePropertiesStore } from "../stores/propertiesStore";
import { useUserFactStore } from "../stores/userFactStore";
import { useViewerStore } from "../stores/viewerStore";
import { useViolationsStore } from "../stores/violationsStore";
import { AccHeaderContext } from "./AccHeaderContext";
import { AccProjectSourceCard } from "./AccProjectSourceCard";
import { ElementInspector } from "./ElementInspector";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { QuarantinedRulesPanel } from "./QuarantinedRulesPanel";
import { RequirementsIntelligencePanel } from "./RequirementsIntelligencePanel";
import { SheetChecklistCard } from "./SheetChecklistCard";
import { SheetViewer } from "./SheetViewer";
import { SubmissionReadinessPanel } from "./SubmissionReadinessPanel";
import { TopChrome } from "./TopChrome";
import { ViolationsPanel } from "./ViolationsPanel";

export function App() {
  const project = useCurrentProject();
  const accSelection = useAccBrowseStore(s => s.selection);
  const inferredJurisdictionIds = useEngineStore(
    s => s.inferredJurisdictionIds
  );
  const viewerUrn = useAccModelStore(s => s.viewerUrn);
  const accViewables = useAccModelStore(s => s.viewables);
  const currentViewableId = useAccModelStore(s => s.currentViewableId);
  const accModelStatus = useAccModelStore(s => s.status);
  const accModelError = useAccModelStore(s => s.error);
  const accProgress = useAccModelStore(s => s.progressMessage);

  const loadProperties = usePropertiesStore(s => s.loadFromUrl);
  const loadEngine = useEngineStore(s => s.loadFromUrls);
  const loadRulesFromUrl = useEngineStore(s => s.loadRulesFromUrl);
  const setCurrentSheet = useViewerStore(s => s.setCurrentSheet);
  const viewer = useViewerStore(s => s.viewer);
  const viewerReady = useViewerStore(s => s.ready);
  const currentSheetId = useViewerStore(s => s.currentSheetId);

  const engineLoaded = useEngineStore(s => s.loaded);
  const rules = useEngineStore(s => s.rules);
  const elements = useEngineStore(s => s.elements);
  const enabledJurisdictions = useEngineStore(s => s.enabledJurisdictions);
  const setActive = useViolationsStore(s => s.setActive);
  const userFactOverrides = useUserFactStore(s => s.overrides);
  const loadUserFacts = useUserFactStore(s => s.loadProject);

  const geometryRows = useGeometryStore(s => s.rows);
  const geometryStatus = useGeometryStore(s => s.status);
  const runGeometryChecks = useGeometryStore(s => s.runGeometryChecks);
  const clearGeometry = useGeometryStore(s => s.clear);

  const accMode = !isAuthDisabled();
  const hasAccJurisdiction = inferredJurisdictionIds.length > 0;
  const liveAccActive = accMode && !!accSelection && hasAccJurisdiction;
  const inferredJurisdictionKey = inferredJurisdictionIds.join("|");

  useAccLiveModel();
  useLiveAccAnalysis();

  const sheetRouting = useMemo(() => {
    if (viewerUrn && accViewables.length > 0) {
      return liveSheetByOntologyType(accViewables);
    }
    return project.defaultSheetByOntologyType;
  }, [viewerUrn, accViewables, project.defaultSheetByOntologyType]);

  const onUniversalView = viewerUrn
    ? accViewables.find(v => v.id === currentViewableId)?.kind === "3d"
    : isUniversalSheet(currentSheetId);

  useEffect(() => {
    if (!accMode) return;
    if (!hasAccJurisdiction) {
      useEngineStore.setState({
        loaded: false,
        loadError: null,
        elements: [],
        rules: [],
        jurisdictions: [],
        enabledJurisdictions: {},
      });
      setActive([]);
      return;
    }
    void loadRulesFromUrl("/rule-pack.json", { inferredJurisdictionIds });
  }, [
    accMode,
    hasAccJurisdiction,
    inferredJurisdictionKey,
    loadRulesFromUrl,
    setActive,
  ]);

  useEffect(() => {
    if (!accMode) return;
    clearGeometry();
  }, [accMode, accSelection?.itemId, clearGeometry]);

  useEffect(() => {
    if (accMode) return;
    loadProperties(project.propertiesUrl);
    void loadEngine("/rule-pack.json", project.elementFactsUrl, {
      inferredJurisdictionIds: project.inferredJurisdictionIds,
    });
    setCurrentSheet(project.defaultSheetId);
    clearGeometry();
    loadUserFacts(project.id);
  }, [
    accMode,
    project,
    loadProperties,
    loadEngine,
    setCurrentSheet,
    clearGeometry,
    loadUserFacts,
  ]);

  useEffect(() => {
    if (!engineLoaded || !viewerReady || !viewer) return;
    if (!enabledJurisdictions["sg-bca"]) {
      clearGeometry();
      return;
    }
    if (!onUniversalView) return;
    void runGeometryChecks(viewer);
  }, [
    engineLoaded,
    viewerReady,
    viewer,
    onUniversalView,
    enabledJurisdictions,
    runGeometryChecks,
    clearGeometry,
  ]);

  useEffect(() => {
    if (!engineLoaded) return;
    const live = computeLiveViolations(
      useEngineStore.getState(),
      sheetRouting,
      userFactOverrides,
      liveAccActive ? "acc-live" : project.id
    );
    const bcaOn = !!enabledJurisdictions["sg-bca"];
    const geo =
      bcaOn && geometryStatus === "done"
        ? geometryViolations(geometryRows)
        : [];
    setActive([...live, ...geo]);
  }, [
    engineLoaded,
    rules,
    elements,
    enabledJurisdictions,
    geometryRows,
    geometryStatus,
    userFactOverrides,
    setActive,
    sheetRouting,
    liveAccActive,
    project.id,
  ]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "320px 1fr 320px",
        gridTemplateRows: "auto 1fr",
        height: "100vh",
        width: "100vw",
      }}
    >
      <header
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          alignItems: "center",
          padding: "8px 16px",
          borderBottom: "1px solid var(--fc2-border)",
          background: "var(--fc2-panel-bg)",
          gap: 16,
        }}
      >
        <strong>{PRODUCT_NAME}</strong>
        <span style={{ color: "var(--fc2-text-dim)", fontSize: 12 }}>
          AEC Permitting Assistant
        </span>
        {isAuthDisabled() && (
          <>
            <span
              aria-hidden
              style={{ color: "var(--fc2-text-dim)", fontSize: 11 }}
            >
              ·
            </span>
            <ProjectSwitcher variant="dark" />
          </>
        )}
        {!isAuthDisabled() && <AccHeaderContext />}
        <div style={{ marginLeft: "auto", flexShrink: 0 }}>
          <TopChrome />
        </div>
      </header>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: 12,
          minHeight: 0,
          overflowY: "auto",
          borderRight: "1px solid var(--fc2-border)",
          background: "var(--fc2-panel-bg)",
        }}
      >
        {liveAccActive && accModelStatus === "loading" && (
          <p style={{ margin: 0, fontSize: 11, color: "var(--fc2-text-dim)" }}>
            {accProgress || "Loading ACC model…"}
          </p>
        )}
        {accModelError && (
          <p style={{ margin: 0, fontSize: 11, color: "#f87171" }}>
            {accModelError}
          </p>
        )}
        {!isAuthDisabled() && <AccProjectSourceCard />}
        <SubmissionReadinessPanel />
        <RequirementsIntelligencePanel />
        <SheetChecklistCard />
        <QuarantinedRulesPanel />
        <ViolationsPanel />
      </div>
      <SheetViewer />
      <ElementInspector />
    </div>
  );
}
